import React, { HtmlHTMLAttributes } from 'react';
import classname from 'classnames';
import { Button as ZarmButton, Popper } from 'zarm';
import { Icon } from '@iyunbao/components';
import { action } from '@iyunbao/cr-engine';

import Button from '../form/button';

import { ActionProps, BaseWidgetProps } from '../utils/type';

interface Title {
  label: string;
  tips?: string;
  style: HtmlHTMLAttributes<string>;
}

interface ModalData {
  title: string;
  content: string;
  initFetch: any;
}

export interface Description {
  label: string;
  style: HtmlHTMLAttributes<string>;
  widget?: "button-modal" | "arrow";
  modal?: ModalData; // button-modal时 modal
  action?: ActionProps; // arrow时 action
  log?: string; // arrow时 log
}

interface CardItem {
  title: Title;
  description: Description;
  style: HtmlHTMLAttributes<string>;
}

interface ExtraWprops {
  label: string;
  description?: string;
  style?: HtmlHTMLAttributes<string>;
  action?: ActionProps;
  className?: string;
  block?: boolean;
  [propName: string]: any;
}

interface Extra {
  widget: "button" | "popper";
  wprops: ExtraWprops;
}

export interface CardListI extends BaseWidgetProps {
  title?: string;
  extra?: Extra;
  childrens: CardItem[];
  style: HtmlHTMLAttributes<string>;
}

/**
 * 容器组件
 * @param props
 */
function CardList(props: CardListI) {
  const { prefixCls = 'crm-card', className, _crengine, style = {}, title = '', extra, childrens, ...rest } = props;

  const cls = classname({
    [`${prefixCls}`]: true,
    className: true,
  });

  /**
   * 按钮事件，跳转，埋点标记，分享，请求
   * @param item
   * @returns
   */
  const onAction = (item: Description) => {
    if (!item.action) {
      return;
    }
    const { onLog } = props;
    const { action: waction } = _crengine;
    const { log } = item;

    // 用于埋点标记，回调
    if (log && onLog) {
      onLog && onLog(log);
    }

    if (waction) {
      waction(item.action);
      return;
    }

    const { type, data } = item.action;

    if (action[type]) {
      action[type](data);
    }
  };

  /**
   * 渲染小卡片标题右侧的额外信息，包含按钮类型和气泡卡片类型
   * @returns
   */
  const renderExtra = () => {
    if (extra) {
      if (extra.widget === 'button') {
        return (
          <div className="crm-card-item-content-description">
            <Button {...extra.wprops} _crengine={_crengine} />
          </div>
        );
      }
      if (extra.widget === 'popper') {
        return (
          <Popper content={extra.wprops.description} destroy trigger="click" className="crm-card-item-content-popper">
            <div className="crm-card-item-content-extra" style={extra.wprops.style}>
              <Icon type="wenhao" style={{ fontSize: 15 }} />
              <span className="crm-card-item-content-extra__text">{extra.wprops.label}</span>
            </div>
          </Popper>
        );
      }
    }
    return null;
  };

  /**
   * 渲染每个卡片的标题
   * @returns
   */
  const renderTitle = () => {
    if (title || extra) {
      const titleCls = classname({
        'crm-card-item-content-label': true,
        'crm-card-item-content-title': !!title,
      });
      return (
        <div className="crm-card-item-content">
          {title && <span className={titleCls}>{title}</span>}
          {renderExtra()}
        </div>
      );
    }
    return null;
  };

  /**
   * 每个卡片配置的点击事件 、
   * 箭头是跳转链接
   * 弹框时打开弹框
   * @param item
   * @returns
   */
  const renderAction = (item) => {
    const { widget, style: descStyle, label: descLabel } = item.description;
    if (widget === 'arrow') {
      return (
        <>
          <div className="crm-card-item-content-description" style={descStyle}>
            {descLabel}
          </div>
          <div className="crm-card-item-content-arrow" style={descStyle}>
            <Icon type="jinru" style={{ fontSize: 13 }} />
          </div>
        </>
      );
    }
    if (widget === 'button') {
      return (
        <ZarmButton className="crm-card-item-content-btn" style={descStyle} onClick={() => onAction(item.description)}>
          {descLabel}
        </ZarmButton>
      );
    }
    return (
      <div className="crm-card-item-content-description" style={descStyle}>
        {descLabel}
      </div>
    );
  };

  return (
    <div className={cls} style={style} {...rest}>
      {renderTitle()}
      {(childrens || []).map((item) => {
        const descWidget = item.description.widget;
        const contentCls = classname({
          'crm-card-item-content': true,
          'crm-card-item-content-pointer': descWidget === 'arrow',
        });
        return (
          <div
            className={contentCls}
            key={`${item.title.label}${item.description.label}`}
            style={item.style}
            onClick={() => (descWidget ? onAction(item.description) : '')}
          >
            <div className="crm-card-item-content-label" style={item.title.style}>
              {item.title.label}
              {item.title.tips && <div className="crm-card-item-content-tips">{item.title.tips.slice(0, 15)}</div>}
            </div>
            {renderAction(item)}
          </div>
        );
      })}
    </div>
  );
}

export default CardList;
